import "./style.css";

import { decodeAudioFile } from "./audio/decode";
import {
  createPolyrhythmMode,
  maxPolyrhythmCount,
  minPolyrhythmCount,
  parsePolyrhythmMode,
} from "./rhythm";
import type { PolyrhythmMode, PolyrhythmRatio } from "./rhythm";
import type { BeatBackendPreference, RhythmMode, SwingOptions } from "./types";
import type { WorkerRequest, WorkerResponse } from "./worker/messages";


const worker = new Worker(new URL("./worker/swing-worker.ts", import.meta.url), { type: "module" });
const manifestPaths = import.meta.env.DEV
  ? ["/model-manifest.json", "/assets/model-manifest.json"]
  : ["/assets/model-manifest.json", "/model-manifest.json"];
const manifestUrls = manifestPaths.map((path) => new URL(path, window.location.origin).toString());
const defaultPreviewBpm = 108;
const previewLeadTimeSec = 0.06;

interface PolyrhythmGroup {
  label: string;
  ratios: PolyrhythmOption[];
}

interface PolyrhythmOption {
  pulses: number;
  beats: number;
  mode: PolyrhythmMode;
}

interface RhythmPreviewEvent {
  beat: number;
  kind: "beat" | "subdivision" | "polyrhythm";
  gain: number;
}

function buildPolyrhythmGroups(): PolyrhythmGroup[] {
  const groups: PolyrhythmGroup[] = [];
  for (let beats = minPolyrhythmCount; beats <= maxPolyrhythmCount; beats += 1) {
    const ratios: PolyrhythmOption[] = [];
    for (let pulses = minPolyrhythmCount; pulses <= maxPolyrhythmCount; pulses += 1) {
      const mode = createPolyrhythmMode(pulses, beats);
      if (mode) {
        ratios.push({ pulses, beats, mode });
      }
    }
    groups.push({ label: `${beats}拍に重ねる`, ratios });
  }
  return groups;
}

const polyrhythmGroups = buildPolyrhythmGroups();
const polyrhythmModes = polyrhythmGroups.flatMap((group) => (
  group.ratios.map(({ mode }) => mode)
));
const rhythmModes: readonly RhythmMode[] = ["eighth", "sixteenth", ...polyrhythmModes];
const polyrhythmOptionsHtml = polyrhythmGroups.map((group) => `
                    <optgroup label="${group.label}">
                      ${group.ratios.map(({ pulses, beats, mode }) => (
                        `<option value="${mode}">ポリリズム ${pulses}:${beats}（${beats}拍に${pulses}つ）</option>`
                      )).join("")}
                    </optgroup>
                  `).join("");

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) {
  throw new Error("Missing #app root element.");
}

app.innerHTML = `
  <div class="splash-screen" aria-hidden="true">
    <div class="splash-panel">
      <span class="splash-note">♪</span>
      <span class="splash-title">ハネリズム</span>
    </div>
  </div>

  <main class="app-shell">
    <section class="intro">
      <div class="app-mark" aria-hidden="true">♪</div>
      <h1>ハネリズム</h1>
      <p class="lead">
        音源から「ハネた！」リズムをつくろう
      </p>
    </section>

    <section class="converter-card">
      <div class="converter-main">
        <label class="dropzone" for="audio-file">
          <span class="dropzone-kicker">音声・動画ファイル</span>
          <strong id="file-name">クリックして音源を選択</strong>
          <span>WAV / MP3 / M4A / MP4 / WebM などに対応</span>
          <input id="audio-file" class="sr-only" type="file" accept="audio/*,video/*,.wav,.mp3,.m4a,.aac,.flac,.ogg,.opus,.mp4,.m4v,.mov,.webm,.ogv" />
        </label>

        <div class="control-stack">
          <div class="swing-control">
            <div class="control-heading">
              <div>
                <span id="alpha-label" class="section-label">ハネ具合</span>
                <p id="alpha-help">まずは 0.50 がおすすめです。</p>
              </div>
              <strong id="alpha-value">0.50</strong>
            </div>
            <input id="alpha-range" type="range" min="0" max="100" step="1" value="50" />
            <div class="range-labels">
              <span id="range-min-label">控えめ</span>
              <span id="range-max-label">しっかり</span>
            </div>
          </div>

          <details id="advanced-settings" class="advanced-settings">
            <summary>詳細設定</summary>
            <div class="advanced-grid">
              <label class="field">
                <span>解析方法</span>
                <select id="backend-select">
                  <option value="auto">自動</option>
                  <option value="madmom-onnx">高精度</option>
                  <option value="spectral-flux-js">速度重視</option>
                </select>
              </label>

              <label class="field">
                <span>リズムの種類</span>
                <select id="subdivision">
                  <optgroup label="ハネリズム">
                    <option value="eighth">スウィング（自然にハネる）</option>
                    <option value="sixteenth">ハーフタイムシャッフル（細かくハネる）</option>
                  </optgroup>
                  ${polyrhythmOptionsHtml}
                </select>
                <small id="rhythm-help" class="field-hint">スウィングは、自然なハネ感を加える基本の設定です。</small>
                <span class="rhythm-preview-row">
                  <span class="rhythm-preview-arrow" aria-hidden="true">→</span>
                  <button id="rhythm-preview-button" class="rhythm-preview-button" type="button" aria-pressed="false">聞いてみる</button>
                </span>
              </label>

              <label class="field">
                <span>BPM を指定</span>
                <input id="manual-bpm" type="number" min="40" max="240" step="0.1" placeholder="自動" />
              </label>

              <label class="field">
                <span>最初の拍位置 ms</span>
                <input id="manual-offset" type="number" min="0" step="1" placeholder="自動" />
              </label>
            </div>
          </details>
        </div>
      </div>

      <div class="action-row">
        <button id="start-button" class="btn-lg-primary main-action" disabled>変換する</button>
        <button id="cancel-button" class="btn-lg-secondary" hidden disabled>中止</button>
      </div>

      <div class="status-area">
        <div id="progress-meter" class="progress-meter" role="progressbar" aria-label="変換の進み具合" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" aria-valuetext="0%" data-progress="0%" hidden></div>
        <p id="status-copy">準備しています。</p>
      </div>

      <section id="result-panel" class="result-panel" hidden>
        <p>変換が完了しました。プレビューして、問題なければ保存してください。</p>
        <audio id="preview-audio" class="preview" controls hidden></audio>
        <a id="download-link" class="btn-lg download-link" hidden>WAV をダウンロード</a>
      </section>

      <section class="trust-row" aria-label="このアプリの特徴">
        <span>※すべての処理はお使いの端末内で完結します。音源がサーバーに送信されることはありません。</span>
      </section>

      <section class="sr-only" aria-hidden="true">
        <strong id="worker-state">Booting</strong>
        <strong id="stage-state">Idle</strong>
        <strong id="backend-state">-</strong>
        <strong id="bpm-value">-</strong>
        <strong id="beat-count">-</strong>
        <strong id="confidence-value">-</strong>
        <strong id="peak-value">-</strong>
        <div id="event-log">Waiting for worker initialization...</div>
      </section>
    </section>
  </main>
`;

const splashScreen = document.querySelector<HTMLElement>(".splash-screen");
if (splashScreen) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      window.setTimeout(() => {
        splashScreen.classList.add("is-hidden");
      }, 2200);
      window.setTimeout(() => {
        splashScreen.remove();
      }, 3000);
    });
  });
}

function requireElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) {
    throw new Error(`Missing required DOM node: ${selector}`);
  }
  return element;
}

const fileInput = requireElement<HTMLInputElement>("#audio-file");
const backendSelect = requireElement<HTMLSelectElement>("#backend-select");
const subdivisionSelect = requireElement<HTMLSelectElement>("#subdivision");
const manualBpmInput = requireElement<HTMLInputElement>("#manual-bpm");
const manualOffsetInput = requireElement<HTMLInputElement>("#manual-offset");
const alphaRange = requireElement<HTMLInputElement>("#alpha-range");
const alphaLabel = requireElement<HTMLElement>("#alpha-label");
const alphaHelp = requireElement<HTMLElement>("#alpha-help");
const alphaValue = requireElement<HTMLElement>("#alpha-value");
const rhythmHelp = requireElement<HTMLElement>("#rhythm-help");
const rhythmPreviewButton = requireElement<HTMLButtonElement>("#rhythm-preview-button");
const rangeMinLabel = requireElement<HTMLElement>("#range-min-label");
const rangeMaxLabel = requireElement<HTMLElement>("#range-max-label");
const startButton = requireElement<HTMLButtonElement>("#start-button");
const cancelButton = requireElement<HTMLButtonElement>("#cancel-button");
const downloadLink = requireElement<HTMLAnchorElement>("#download-link");
const previewAudio = requireElement<HTMLAudioElement>("#preview-audio");
const workerState = requireElement<HTMLElement>("#worker-state");
const stageState = requireElement<HTMLElement>("#stage-state");
const backendState = requireElement<HTMLElement>("#backend-state");
const bpmValue = requireElement<HTMLElement>("#bpm-value");
const beatCount = requireElement<HTMLElement>("#beat-count");
const confidenceValue = requireElement<HTMLElement>("#confidence-value");
const peakValue = requireElement<HTMLElement>("#peak-value");
const logView = requireElement<HTMLElement>("#event-log");
const fileName = requireElement<HTMLElement>("#file-name");
const statusCopy = requireElement<HTMLElement>("#status-copy");
const progressMeter = requireElement<HTMLElement>("#progress-meter");
const resultPanel = requireElement<HTMLElement>("#result-panel");

let ready = false;
let outputUrl: string | null = null;
let rhythmPreviewContext: AudioContext | null = null;
let rhythmPreviewTimer: number | null = null;
let configuredBackends: BeatBackendPreference[] = ["auto", "spectral-flux-js"];
let displayedProgress = 0;

function appendLog(message: string): void {
  const timestamp = new Date().toLocaleTimeString();
  logView.textContent = `[${timestamp}] ${message}\n${logView.textContent}`;
}

function setStatus(message: string): void {
  statusCopy.textContent = message;
}

function setProgress(value: number, options: { reset?: boolean } = {}): void {
  const targetPercent = Math.max(0, Math.min(100, value));
  const percent = options.reset ? targetPercent : Math.max(displayedProgress, targetPercent);
  const roundedPercent = Math.round(percent);
  displayedProgress = percent;
  progressMeter.style.setProperty("--progress", `${percent}%`);
  progressMeter.dataset.progress = `${roundedPercent}%`;
  progressMeter.setAttribute("aria-valuenow", String(roundedPercent));
  progressMeter.setAttribute("aria-valuetext", `${roundedPercent}%`);
}

function setProgressVisible(visible: boolean): void {
  progressMeter.hidden = !visible;
}

function revokeOutputUrl(): void {
  if (outputUrl) {
    URL.revokeObjectURL(outputUrl);
    outputUrl = null;
  }
}

function setRunning(running: boolean): void {
  if (running) {
    stopRhythmPreview();
  }
  startButton.disabled = !ready || running || !(fileInput.files?.length);
  startButton.textContent = running ? "変換中..." : "変換する";
  cancelButton.hidden = !running;
  cancelButton.disabled = !running;
  fileInput.disabled = running;
  backendSelect.disabled = running;
  subdivisionSelect.disabled = running;
  manualBpmInput.disabled = running;
  manualOffsetInput.disabled = running;
  alphaRange.disabled = running;
  rhythmPreviewButton.disabled = running;
}

function parseOptionalNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function updateAlphaLabel(): void {
  alphaValue.textContent = (Number(alphaRange.value) / 100).toFixed(2);
}

function getAudioContextConstructor(): typeof AudioContext {
  const constructor = window.AudioContext ?? window.webkitAudioContext;
  if (!constructor) {
    throw new Error("このブラウザではリズム試聴に必要な Web Audio API を利用できません。");
  }
  return constructor;
}

function isRhythmMode(value: string): value is RhythmMode {
  return rhythmModes.includes(value as RhythmMode);
}

function selectedRhythmMode(): RhythmMode {
  return isRhythmMode(subdivisionSelect.value) ? subdivisionSelect.value : "eighth";
}

function mapSourceFraction(
  sourceFraction: number,
  points: Array<readonly [number, number]>,
): number {
  for (let index = 0; index + 1 < points.length; index += 1) {
    const [sourceLeft, targetLeft] = points[index];
    const [sourceRight, targetRight] = points[index + 1];
    if (sourceFraction <= sourceRight) {
      const ratio = (sourceFraction - sourceLeft) / Math.max(1e-6, sourceRight - sourceLeft);
      return targetLeft + ((targetRight - targetLeft) * ratio);
    }
  }
  return points[points.length - 1][1];
}

function buildSwingPreviewEvents(alpha: number): RhythmPreviewEvent[] {
  const offbeat = mapSourceFraction(0.5, [
    [0, 0],
    [1 / 3, (1 + alpha) / 3],
    [1, 1],
  ]);
  const events: RhythmPreviewEvent[] = [];

  for (let beat = 0; beat < 4; beat += 1) {
    events.push({ beat, kind: "beat", gain: beat === 0 ? 1 : 0.72 });
    events.push({ beat: beat + offbeat, kind: "subdivision", gain: 0.55 });
  }

  return events;
}

function buildShufflePreviewEvents(alpha: number): RhythmPreviewEvent[] {
  const points: Array<readonly [number, number]> = [
    [0, 0],
    [1 / 6, (1 + alpha) / 6],
    [1 / 2, 1 / 2],
    [4 / 6, (1 / 2) + ((1 + alpha) / 6)],
    [1, 1],
  ];
  const events: RhythmPreviewEvent[] = [];

  for (let beat = 0; beat < 4; beat += 1) {
    events.push({ beat, kind: "beat", gain: beat === 0 ? 1 : 0.68 });
    events.push({ beat: beat + mapSourceFraction(0.25, points), kind: "subdivision", gain: 0.42 });
    events.push({ beat: beat + 0.5, kind: "beat", gain: 0.36 });
    events.push({ beat: beat + mapSourceFraction(0.75, points), kind: "subdivision", gain: 0.42 });
  }

  return events;
}

function buildPolyrhythmPreviewEvents(
  ratio: PolyrhythmRatio,
  alpha: number,
): RhythmPreviewEvent[] {
  const cycleCount = ratio.beats <= 4 ? 2 : 1;
  const events: RhythmPreviewEvent[] = [];
  const polyrhythmGain = 0.28 + (alpha * 0.34);

  for (let cycle = 0; cycle < cycleCount; cycle += 1) {
    const offset = cycle * ratio.beats;
    for (let beat = 0; beat < ratio.beats; beat += 1) {
      events.push({ beat: offset + beat, kind: "beat", gain: beat === 0 ? 0.92 : 0.54 });
    }
    for (let pulse = 0; pulse < ratio.pulses; pulse += 1) {
      events.push({
        beat: offset + ((pulse * ratio.beats) / ratio.pulses),
        kind: "polyrhythm",
        gain: pulse === 0 ? polyrhythmGain + 0.12 : polyrhythmGain,
      });
    }
  }

  return events;
}

function buildRhythmPreviewEvents(mode: RhythmMode, alpha: number): RhythmPreviewEvent[] {
  const polyrhythmRatio = parsePolyrhythmMode(mode);
  if (polyrhythmRatio) {
    return buildPolyrhythmPreviewEvents(polyrhythmRatio, alpha);
  }
  if (mode === "sixteenth") {
    return buildShufflePreviewEvents(alpha);
  }
  return buildSwingPreviewEvents(alpha);
}

function rhythmPreviewLoopBeats(mode: RhythmMode): number {
  const polyrhythmRatio = parsePolyrhythmMode(mode);
  if (polyrhythmRatio) {
    return polyrhythmRatio.beats * (polyrhythmRatio.beats <= 4 ? 2 : 1);
  }
  return 4;
}

function schedulePreviewClick(
  context: AudioContext,
  time: number,
  event: RhythmPreviewEvent,
): void {
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  const frequency = event.kind === "beat" ? 260 : event.kind === "polyrhythm" ? 880 : 620;
  const duration = event.kind === "beat" ? 0.055 : 0.038;
  const peakGain = 0.16 * event.gain;

  oscillator.type = event.kind === "polyrhythm" ? "triangle" : "sine";
  oscillator.frequency.setValueAtTime(frequency, time);
  gain.gain.setValueAtTime(0.0001, time);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, peakGain), time + 0.004);
  gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);

  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(time);
  oscillator.stop(time + duration + 0.02);
}

function scheduleRhythmPreviewLoop(context: AudioContext, loopStartTime: number): void {
  if (rhythmPreviewContext !== context) {
    return;
  }

  const mode = selectedRhythmMode();
  const alpha = Number(alphaRange.value) / 100;
  const bpm = Math.max(60, Math.min(160, parseOptionalNumber(manualBpmInput.value) ?? defaultPreviewBpm));
  const beatSec = 60 / bpm;
  const events = buildRhythmPreviewEvents(mode, alpha);
  const loopSec = rhythmPreviewLoopBeats(mode) * beatSec;
  const startTime = Math.max(loopStartTime, context.currentTime + previewLeadTimeSec);

  for (const event of events) {
    schedulePreviewClick(context, startTime + (event.beat * beatSec), event);
  }

  const nextStartTime = startTime + loopSec;
  rhythmPreviewTimer = window.setTimeout(() => {
    scheduleRhythmPreviewLoop(context, nextStartTime);
  }, Math.max(0, (nextStartTime - context.currentTime - previewLeadTimeSec) * 1000));
}

function stopRhythmPreview(): void {
  if (rhythmPreviewTimer !== null) {
    window.clearTimeout(rhythmPreviewTimer);
    rhythmPreviewTimer = null;
  }

  if (rhythmPreviewContext) {
    void rhythmPreviewContext.close();
    rhythmPreviewContext = null;
  }

  rhythmPreviewButton.textContent = "聞いてみる";
  rhythmPreviewButton.setAttribute("aria-pressed", "false");
}

async function playRhythmPreview(): Promise<void> {
  stopRhythmPreview();

  const AudioContextConstructor = getAudioContextConstructor();
  const context = new AudioContextConstructor();
  rhythmPreviewContext = context;

  try {
    await context.resume();
    rhythmPreviewButton.textContent = "停止する";
    rhythmPreviewButton.setAttribute("aria-pressed", "true");
    scheduleRhythmPreviewLoop(context, context.currentTime + previewLeadTimeSec);
  } catch (error) {
    stopRhythmPreview();
    const detail = error instanceof Error ? error.message : String(error);
    appendLog(`Rhythm preview failed: ${detail}`);
    setStatus(`リズムを試聴できませんでした。${detail}`);
  }
}

function updateRhythmModeCopy(): void {
  const rhythmMode = selectedRhythmMode();
  const polyrhythmRatio = parsePolyrhythmMode(rhythmMode);

  if (polyrhythmRatio) {
    alphaLabel.textContent = "強さ";
    alphaHelp.textContent = "ポリリズムの揺れに近づけます。複雑な比率は控えめがおすすめです。";
    rhythmHelp.textContent = `ポリリズム ${polyrhythmRatio.pulses}:${polyrhythmRatio.beats} は、${polyrhythmRatio.beats}拍の長さに${polyrhythmRatio.pulses}つの揺れを重ねます。`;
    rangeMinLabel.textContent = "自然";
    rangeMaxLabel.textContent = "強め";
    return;
  }

  rhythmHelp.textContent = rhythmMode === "sixteenth"
    ? "ハーフタイムシャッフルは、細かい刻みをゆったりハネさせます。"
    : "スウィングは、自然なハネ感を加える基本の設定です。";
  alphaLabel.textContent = "ハネ具合";
  alphaHelp.textContent = "まずは 0.50 がおすすめです。";
  rangeMinLabel.textContent = "控えめ";
  rangeMaxLabel.textContent = "しっかり";
}

function updateSelectedFileLabel(): void {
  const file = fileInput.files?.[0];
  fileName.textContent = file ? file.name : "クリックして音源を選択";
  if (file && ready) {
    setStatus("ファイルを読み込みました。リズムを決めて変換できます。");
  }
}

function syncBackendSelect(): void {
  for (const option of Array.from(backendSelect.options)) {
    const value = option.value as BeatBackendPreference;
    option.disabled = !configuredBackends.includes(value);
  }

  const currentValue = backendSelect.value as BeatBackendPreference;
  if (!configuredBackends.includes(currentValue)) {
    backendSelect.value = configuredBackends[0] ?? "auto";
  }
}

function currentOptions(): SwingOptions {
  const preferredBackend = backendSelect.value as BeatBackendPreference;
  return {
    alpha: Number(alphaRange.value) / 100,
    subdivision: selectedRhythmMode(),
    manualBpm: parseOptionalNumber(manualBpmInput.value),
    manualOffsetSec: (() => {
      const milliseconds = parseOptionalNumber(manualOffsetInput.value);
      return milliseconds === null ? null : milliseconds / 1000;
    })(),
    backendPreference: configuredBackends.includes(preferredBackend) ? preferredBackend : "auto",
  };
}

function send(message: WorkerRequest, transfer?: Transferable[]): void {
  worker.postMessage(message, transfer ?? []);
}

function progressFromWorkerStage(stage: string, detail: string): number {
  if (stage === "boot") {
    return 4;
  }
  if (stage === "analysis" || stage === "madmom-pre" || stage === "madmom-onnx") {
    return 35;
  }
  if (stage === "render") {
    const match = detail.match(/Rendering\s+(\d+)\/(\d+)/);
    if (match) {
      const completed = Number(match[1]);
      const total = Number(match[2]);
      if (Number.isFinite(completed) && Number.isFinite(total) && total > 0) {
        return 45 + ((completed / total) * 40);
      }
    }
    return 55;
  }
  if (stage === "encode") {
    return 92;
  }
  return 20;
}

function messageFromWorkerStage(stage: string): string {
  if (stage === "boot") {
    return "変換エンジンを準備しています。";
  }
  if (stage === "analysis" || stage === "madmom-pre" || stage === "madmom-onnx") {
    return "ビートを解析しています。";
  }
  if (stage === "render") {
    return "音色を保ちながら、リズムを調整しています。";
  }
  if (stage === "encode") {
    return "WAV ファイルを書き出しています。";
  }
  return "処理しています。";
}

worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
  const message = event.data;

  if (message.type === "ready") {
    ready = true;
    configuredBackends = message.configuredBackends;
    syncBackendSelect();
    workerState.textContent = "Ready";
    stageState.textContent = "Idle";
    backendState.textContent = message.defaultBackend;
    backendSelect.value = configuredBackends.includes("auto") ? "auto" : backendSelect.value;
    appendLog(`Worker ready. Configured backends: ${configuredBackends.join(", ")}.`);
    setStatus(fileInput.files?.length ? "ファイルを読み込みました。変換できます。" : "まずは音源を選んでください。");
    setProgress(0, { reset: true });
    setProgressVisible(false);
    setRunning(false);
    return;
  }

  if (message.type === "progress") {
    stageState.textContent = message.stage;
    appendLog(message.detail);
    setStatus(messageFromWorkerStage(message.stage));
    setProgressVisible(true);
    setProgress(progressFromWorkerStage(message.stage, message.detail));
    return;
  }

  if (message.type === "result") {
    revokeOutputUrl();
    outputUrl = URL.createObjectURL(new Blob([message.wav], { type: "audio/wav" }));
    downloadLink.href = outputUrl;
    downloadLink.download = message.outputName;
    downloadLink.hidden = false;
    previewAudio.src = outputUrl;
    previewAudio.hidden = false;
    resultPanel.hidden = false;

    workerState.textContent = "Ready";
    stageState.textContent = "Done";
    backendState.textContent = message.analysis.backend;
    bpmValue.textContent = message.analysis.detectedBpm.toFixed(1);
    beatCount.textContent = String(message.analysis.beatTimesSec.length);
    confidenceValue.textContent = `${Math.min(100, message.analysis.confidence * 100).toFixed(0)}%`;
    peakValue.textContent = message.peakLevel.toFixed(3);
    appendLog(`Rendered ${message.outputName} using ${message.analysis.backend}.`);
    setStatus("変換が完了しました。プレビューとダウンロードができます。");
    setProgress(100);
    setProgressVisible(false);
    setRunning(false);
    return;
  }

  if (message.type === "error") {
    workerState.textContent = "Error";
    stageState.textContent = "Stopped";
    appendLog(`ERROR: ${message.detail}`);
    setStatus(`変換できませんでした。${message.detail}`);
    setProgress(0, { reset: true });
    setProgressVisible(false);
    setRunning(false);
  }
};

fileInput.addEventListener("change", () => {
  updateSelectedFileLabel();
  setRunning(false);
});

alphaRange.addEventListener("input", updateAlphaLabel);
subdivisionSelect.addEventListener("change", () => {
  stopRhythmPreview();
  updateRhythmModeCopy();
});
rhythmPreviewButton.addEventListener("click", () => {
  if (rhythmPreviewContext) {
    stopRhythmPreview();
    return;
  }
  void playRhythmPreview();
});
updateAlphaLabel();
updateRhythmModeCopy();
syncBackendSelect();
setStatus("準備しています。");

startButton.addEventListener("click", async () => {
  const file = fileInput.files?.[0];
  if (!file) {
    setStatus("先に音声・動画ファイルを選んでください。");
    appendLog("Select an audio or video file first.");
    return;
  }
  if (!ready) {
    setStatus("準備中です。少し待ってから変換してください。");
    appendLog("Worker is still booting.");
    return;
  }

  workerState.textContent = "Preparing";
  stageState.textContent = "decode";
  revokeOutputUrl();
  downloadLink.hidden = true;
  previewAudio.hidden = true;
  resultPanel.hidden = true;
  bpmValue.textContent = "-";
  beatCount.textContent = "-";
  confidenceValue.textContent = "-";
  peakValue.textContent = "-";
  setStatus("ファイルの音声を読み込んでいます。");
  setProgressVisible(true);
  setProgress(8, { reset: true });
  setRunning(true);

  try {
    appendLog(`Decoding audio track from ${file.name} in the main thread.`);
    const decoded = await decodeAudioFile(file);
    appendLog(`Decoded ${decoded.channelData.length} channel(s) at ${decoded.sampleRate} Hz.`);

    const channelBuffers = decoded.channelData.map((channel) => channel.buffer as ArrayBuffer);
    workerState.textContent = "Running";
    stageState.textContent = "queued";
    setStatus("ビート解析を開始します。");
    setProgress(15);

    send({
      type: "process",
      fileName: file.name,
      sampleRate: decoded.sampleRate,
      channels: channelBuffers,
      options: currentOptions(),
    }, channelBuffers);
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    workerState.textContent = "Error";
    stageState.textContent = "Stopped";
    appendLog(`ERROR: ${detail}`);
    setStatus(`ファイルの音声を読み込めませんでした。${detail}`);
    setProgress(0, { reset: true });
    setProgressVisible(false);
    setRunning(false);
  }
});

cancelButton.addEventListener("click", () => {
  appendLog("Cancellation requested.");
  send({ type: "cancel" });
  workerState.textContent = "Stopping";
  stageState.textContent = "cancel";
  setStatus("変換を中止しています。");
});

send({ type: "init", manifestUrls });
